import { setAppBarTitle } from 'ducks/ui';
import React from 'react';
import { connect } from 'react-redux';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router-dom';

/*
 * The guard, as one layout route rather than thirty-one wrappers.
 *
 * Router 5 let a component stand in for `<Route>`, so `PrivateRoute` could ask
 * about authentication and render either the page or a redirect, once per route.
 * A data router builds its routes as objects before anything renders, and a
 * component in that position never gets asked - so the guard becomes the parent
 * of the private routes, and the page arrives through `<Outlet/>`.
 *
 * The title travels with the route now, in `handle`, and is read back with
 * `useMatches`. Same strings, same screens, same store action - see router.js.
 */
const Guard = ({ isAuthenticated, accountId, setAppBarTitle: setTitle }) => {
  const location = useLocation();
  const matches = useMatches();

  const title = [...matches].reverse().find(m => m.handle && m.handle.title);

  React.useEffect(() => {
    // In an effect, not in the body: router 5 set the title while rendering the
    // route, which React 19 reports as updating one component while rendering
    // another. Same moment for the user, one tick later for the store.
    setTitle(title ? title.handle.title : undefined);
  }, [title, setTitle]);

  if (!isAuthenticated) {
    return <Navigate to='/login' replace state={{ from: location }}/>;
  }
  // Remount on a switch, without leaving the URL. Emptying the store does not
  // reach what a component holds - an open drawer, a selected row - and those
  // point at the previous customer's ids.
  return <Outlet key={accountId}/>;
};

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  accountId: state.auth.account_id
});

const mapDispatchToProps = dispatch => ({
  setAppBarTitle: title => dispatch(setAppBarTitle(title))
});

export default connect(mapStateToProps, mapDispatchToProps)(Guard);
